import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  BarChart3,
  Settings,
  Tag,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface TutorialStep {
  title: string;
  description: string;
  icon: React.ElementType;
  route: string;
  sidebarSelector: string;
  color: string;
}

const VISAO_GERAL_STEP: TutorialStep = {
  title: "Visão Geral",
  description:
    "Aqui é o seu painel principal! Acompanhe KPIs, gráficos de solicitações, alertas e tudo que está acontecendo na ouvidoria em tempo real.",
  icon: LayoutDashboard,
  route: "/",
  sidebarSelector: '[href="/"]',
  color: "primary",
};

const SOLICITACOES_STEP: TutorialStep = {
  title: "Solicitações",
  description:
    "Gerencie todas as demandas dos cidadãos: reclamações, denúncias, sugestões e elogios. Filtre por status, prioridade e acompanhe o SLA de cada uma.",
  icon: FileText,
  route: "/solicitacoes",
  sidebarSelector: '[href="/solicitacoes"]',
  color: "secondary",
};

const AGENTES_STEP: TutorialStep = {
  title: "Agentes",
  description:
    "Veja e gerencie sua equipe de atendimento. Convide novos agentes via link compartilhável e acompanhe a performance de cada membro.",
  icon: Users,
  route: "/agentes",
  sidebarSelector: '[href="/agentes"]',
  color: "tertiary",
};

const OUVIDORIA_STEP: TutorialStep = {
  title: "Ouvidoria",
  description:
    "Gerencie os dados da sua ouvidoria, configurações, secretaria vinculada e informações institucionais.",
  icon: Building2,
  route: "/ouvidoria",
  sidebarSelector: '[href="/ouvidoria"]',
  color: "primary",
};

const TOPICOS_STEP: TutorialStep = {
  title: "Tópicos",
  description:
    "Organize as solicitações por tópicos e categorias. Crie etiquetas para facilitar a triagem e o encaminhamento das demandas.",
  icon: Tag,
  route: "/topicos",
  sidebarSelector: '[href="/topicos"]',
  color: "secondary",
};

const GRAFICOS_STEP: TutorialStep = {
  title: "Gráficos",
  description:
    "Acompanhe dashboards e análises visuais sobre categorias, status, agentes e evolução das demandas da ouvidoria.",
  icon: BarChart3,
  route: "/graficos",
  sidebarSelector: '[href="/graficos"]',
  color: "tertiary",
};

const RELATORIOS_STEP: TutorialStep = {
  title: "Relatórios",
  description:
    "Acesse relatórios detalhados com métricas de desempenho, tempo de resposta, satisfação e tendências da ouvidoria.",
  icon: BarChart3,
  route: "/relatorios",
  sidebarSelector: '[href="/relatorios"]',
  color: "tertiary",
};

const CONFIGURACOES_STEP: TutorialStep = {
  title: "Configurações",
  description:
    "Personalize o sistema de acordo com as necessidades da sua ouvidoria: notificações, permissões, SLAs e muito mais.",
  icon: Settings,
  route: "/configuracoes",
  sidebarSelector: '[href="/configuracoes"]',
  color: "primary",
};

const buildSteps = (isGestor: boolean): TutorialStep[] => [
  VISAO_GERAL_STEP,
  SOLICITACOES_STEP,
  AGENTES_STEP,
  ...(isGestor ? [OUVIDORIA_STEP] : []),
  TOPICOS_STEP,
  GRAFICOS_STEP,
  RELATORIOS_STEP,
  CONFIGURACOES_STEP,
];

interface OnboardingTutorialProps {
  active: boolean;
  onFinish: () => void;
  isGestor: boolean;
}

export function OnboardingTutorial({ active, onFinish, isGestor }: OnboardingTutorialProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const navigate = useNavigate();

  const steps = useMemo(() => buildSteps(isGestor), [isGestor]);

  const step = steps[currentStep];

  const updateSpotlight = useCallback(() => {
    if (!active) return;
    const el = document.querySelector(`nav a${steps[currentStep].sidebarSelector}`);
    if (el) {
      setSpotlightRect(el.getBoundingClientRect());
    }
  }, [active, currentStep]);

  useEffect(() => {
    if (!active) return;
    navigate(steps[currentStep].route);
    // Small delay to let the page render before measuring
    const t = setTimeout(updateSpotlight, 150);
    window.addEventListener("resize", updateSpotlight);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", updateSpotlight);
    };
  }, [active, currentStep, navigate, updateSpotlight]);

  const next = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      onFinish();
    }
  };

  const prev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const isLast = currentStep === steps.length - 1;

  // Calculate tooltip position
  const tooltipStyle: React.CSSProperties = {};
  if (spotlightRect) {
    tooltipStyle.position = "fixed";
    tooltipStyle.left = spotlightRect.right + 20;
    tooltipStyle.top = spotlightRect.top - 10;
    // Make sure it doesn't go off screen
    if (spotlightRect.right + 420 > window.innerWidth) {
      tooltipStyle.left = spotlightRect.left - 400;
    }
  }

  return (
    <AnimatePresence>
      {active && (
        <>
          {/* Dark overlay with spotlight cutout */}
          <motion.div
            className="fixed inset-0 z-[90]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <svg className="absolute inset-0 w-full h-full">
              <defs>
                <mask id="spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  {spotlightRect && (
                    <motion.rect
                      x={spotlightRect.left - 6}
                      y={spotlightRect.top - 4}
                      width={spotlightRect.width + 12}
                      height={spotlightRect.height + 8}
                      rx="10"
                      fill="black"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="hsl(var(--background) / 0.85)"
                mask="url(#spotlight-mask)"
              />
            </svg>

            {/* Glowing ring around spotlight */}
            {spotlightRect && (
              <motion.div
                className="absolute rounded-xl pointer-events-none"
                style={{
                  left: spotlightRect.left - 8,
                  top: spotlightRect.top - 6,
                  width: spotlightRect.width + 16,
                  height: spotlightRect.height + 12,
                  boxShadow: `0 0 0 3px hsl(var(--${step.color})), 0 0 20px hsl(var(--${step.color}) / 0.4)`,
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                key={currentStep}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              />
            )}
          </motion.div>

          {/* Tooltip balloon */}
          {spotlightRect && (
            <motion.div
              className="fixed z-[95] w-[380px]"
              style={tooltipStyle}
              initial={{ opacity: 0, x: -20, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -20 }}
              key={`tooltip-${currentStep}`}
              transition={{ type: "spring", stiffness: 350, damping: 30, delay: 0.1 }}
            >
              <div className="relative rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden">
                {/* Top gradient bar */}
                <div className={`h-1 bg-gradient-to-r from-primary to-secondary`} />

                {/* Arrow pointing to sidebar */}
                <div
                  className="absolute left-[-8px] top-8 w-0 h-0"
                  style={{
                    borderTop: "8px solid transparent",
                    borderBottom: "8px solid transparent",
                    borderRight: "8px solid hsl(var(--card))",
                  }}
                />

                <div className="p-5">
                  {/* Step counter */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`h-9 w-9 rounded-xl bg-${step.color}/15 flex items-center justify-center`}>
                        <step.icon className={`h-5 w-5 text-${step.color}`} />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {currentStep + 1} de {steps.length}
                      </span>
                    </div>
                    <button
                      onClick={onFinish}
                      className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Title */}
                  <h3 className="font-heading text-lg font-bold text-foreground mb-2">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    {step.description}
                  </p>

                  {/* Progress dots */}
                  <div className="flex items-center gap-1.5 mb-4">
                    {steps.map((_, i) => (
                      <div
                        key={i}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === currentStep
                            ? `w-6 bg-${step.color}`
                            : i < currentStep
                            ? "w-1.5 bg-primary/40"
                            : "w-1.5 bg-muted"
                        }`}
                      />
                    ))}
                  </div>

                  {/* Buttons */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={prev}
                      disabled={currentStep === 0}
                      className="gap-1 text-xs"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />
                      Anterior
                    </Button>

                    <Button
                      size="sm"
                      onClick={next}
                      className="gap-1 text-xs rounded-full px-5 bg-gradient-to-r from-primary to-secondary"
                    >
                      {isLast ? (
                        <>
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Concluir
                        </>
                      ) : (
                        <>
                          Próximo
                          <ArrowRight className="h-3.5 w-3.5" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
